import { useEffect, useState } from 'react';
import { StyledProjectSection } from './style';
import { ProjectsList } from './ProjectsList';
import { Tabs } from 'ui';
import { useProjectState } from 'contexts/ProjectContext';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useParticipationBudgetState } from 'contexts/ParticipationBudgetContext';
import useQueryApiClient from '../../../../../../../utils/useQueryApiClient';
import { MapLayerType } from '../../../../../../MapLayers';

export type ProjectState =
  | 'in_voting'
  | 'voting_is_closed'
  | 'supported'
  | 'being_implemented'
  | 'realized'
  | 'not_supported';

interface StatusProps {
  id: string;
  value: ProjectState;
  name: string;
}

export interface ProjectSectionProps {
  onTabChange?: (activeKey: string) => void;
  onProjectListChange?: (visibleProjects: any[]) => void;
  onEmpty?: () => void;
}

export const ProjectSection = ({
  onTabChange = () => {},
  onProjectListChange = () => {},
  onEmpty,
}: ProjectSectionProps) => {
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [years, setYears] = useState<string[]>();
  const [municipalProject, setMunicipalProject] = useState<any>({});
  const [municipalInfo, setMunicipalInfo] = useState<any>({});

  const { budgets, initialized: budgetsInitialized } = useParticipationBudgetState();
  let [searchParams] = useSearchParams();
  const isModalOpen = searchParams.get('municipal-modal');

  const { appendData, initialized: projectsInitialized, projects } = useProjectState();

  const { appendData: getMunicipalInfo } = useQueryApiClient<MapLayerType[]>({
    request: {
      url: `api/v1/tapis/participation_budgets/:id/info`,
      disableOnMount: true,
    },
    onSuccess: (response) => setMunicipalInfo(response),
  });

  const excludedStatuses = ['submitted', 'did_not_qualify', 'ready_to_vote'];
  const validProjects = projects?.filter((project: any) => !excludedStatuses.includes(project.state));

  const publicStatus: StatusProps[] = [
    { id: '1', value: 'in_voting', name: 'Balsošanā' },
    { id: '2', value: 'voting_is_closed', name: 'Balsošana noslēgusies' },
    { id: '3', value: 'supported', name: 'Atbalstīts' },
    { id: '4', value: 'being_implemented', name: 'Tiek īstenots' },
    { id: '5', value: 'realized', name: 'Realizēts' },
    { id: '6', value: 'not_supported', name: 'Neatbalstīts' },
  ];

  const handleTabChange = (year: string) => {
    setSelectedYear(year);
    onTabChange(year);
  };

  useEffect(() => {
    const atvkId = searchParams.get('atvk_id');

    if (atvkId && isModalOpen !== 'open') {
      const status = publicStatus.map((el) => el.value);
      const municipalProjects = budgets.filter((e: any) => e.atvk_id === atvkId);
      const today = dayjs();
      const calculatedYears = municipalProjects
        .filter(({ submission_period_to }: any) => today.isAfter(dayjs(submission_period_to)))
        .map(({ year }: any) => year);
      const municipalProject =
        municipalProjects.find((e: any) => String(e.year) === String(selectedYear)) || municipalProjects[0] || {};
      const isInVoting = dayjs(municipalProject.voting_period_to || today).isAfter(today);
      setMunicipalProject(municipalProject);

      if (municipalProject.id) {
        getMunicipalInfo([], { id: municipalProject.id });
      }

      appendData({
        search: {
          atvk_id: atvkId,
          year: [selectedYear || calculatedYears[0]],
          state: status,
          count_of_votes: true,
          show_winners: true,
        },
      });
      setYears(calculatedYears);

      if (!calculatedYears?.length && selectedYear) {
        onEmpty && onEmpty();
      }

      if (!selectedYear) {
        setSelectedYear(calculatedYears[0]);
      }
    }
  }, [searchParams, projectsInitialized, budgetsInitialized, selectedYear]);

  useEffect(() => {
    onProjectListChange(validProjects);
  }, [JSON.stringify(validProjects)]);

  return (
    <StyledProjectSection className="project-section">
      <Tabs
        type="card"
        activeKey={selectedYear}
        className="year_tabs"
        onChange={handleTabChange}
        items={years?.map((year) => ({
          label: year,
          key: year,
          children: (
            <ProjectsList
              data={{ projects: validProjects }}
              municipalProject={municipalProject}
              municipalInfo={municipalInfo}
            />
          ),
        }))}
      />
    </StyledProjectSection>
  );
};
